import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyUserPoints() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // filters
  const [search, setSearch] = useState('');
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');
  const [minPoints, setMinPoints] = useState('');
  const [maxPoints, setMaxPoints] = useState('');

  // per-column header search
  const [colUserId, setColUserId] = useState('');
  const [colUsername, setColUsername] = useState('');
  const [colPoints, setColPoints] = useState('');

  // sorting
  const [sortBy, setSortBy] = useState('POINTS');
  const [sortOrder, setSortOrder] = useState('DESC');

  const token = localStorage.getItem('token');

  const buildParams = (overrides = {}) => {
    const params = new URLSearchParams();
    const _page = overrides.page ?? page;
    const _limit = overrides.limit ?? limit;

    params.set('page', _page);
    params.set('limit', _limit);

    if (search) params.set('search', search.trim());
    if (username) params.set('username', username.trim());
    if (userId) params.set('userId', userId.trim());
    if (minPoints) params.set('minPoints', minPoints);
    if (maxPoints) params.set('maxPoints', maxPoints);

    // header filters override
    if (colUserId) params.set('userId', colUserId.trim());
    if (colUsername) params.set('username', colUsername.trim());
    if (colPoints) params.set('minPoints', colPoints.trim());

    params.set('sortBy', sortBy);
    params.set('sortOrder', sortOrder);

    return params;
  };

  async function fetchData() {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/company/user-points?${buildParams().toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('Fetch user-points failed:', err);
      alert('Failed to fetch user points');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, limit, sortBy, sortOrder]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchData();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, username, userId, minPoints, maxPoints, colUserId, colUsername, colPoints]);

  const changeSort = (col) => {
    if (sortBy === col) {
      setSortOrder((o) => (o === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  // ---------- Excel export ----------
  const exportRowsToExcel = (data, filename) => {
    // Export only visible columns in the table:
    const exportRows = data.map((r) => ({
      USER_POINTS_ID: r.USER_POINTS_ID,
      USER_ID: r.USER_ID,
      Username: r.username || '',
      POINTS: r.POINTS,
      FIRST_NAME: r.FIRST_NAME || '',
      EMAIL_ID: r.EMAIL_ID || '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'UserPoints');
    XLSX.writeFile(wb, filename);
  };

  const handleExportPage = () => {
    exportRowsToExcel(rows, `user-points_page-${page}.xlsx`);
  };

  const handleExportAll = async () => {
    try {
      // fetch page 1 to know totalPages if needed
      const paramsFirst = buildParams({ page: 1 });
      const firstRes = await axios.get(
        `${BACKEND_URL}/api/company/user-points?${paramsFirst.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const all = [...(firstRes.data.rows || [])];
      const pages = firstRes.data.totalPages || 1;

      // loop remaining pages (if any)
      for (let p = 2; p <= pages; p++) {
        const paramsLoop = buildParams({ page: p });
        const res = await axios.get(
          `${BACKEND_URL}/api/company/user-points?${paramsLoop.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        all.push(...(res.data.rows || []));
      }

      exportRowsToExcel(all, `user-points_all_${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch (err) {
      console.error('Export all failed:', err);
      alert('Failed to export all rows');
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">User Points</h1>
        <div className="flex gap-2">
          <button onClick={handleExportPage} className="px-3 py-1 border rounded">
            Export Page (Excel)
          </button>
          <button onClick={handleExportAll} className="px-3 py-1 border rounded">
            Export All (Excel)
          </button>
        </div>
      </div>

      {/* Common Filters */}
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="flex flex-col">
          <label className="text-sm">Common Search</label>
          <input
            className="border px-2 py-1 rounded"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="username / name / email"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Username</label>
          <input
            className="border px-2 py-1 rounded"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">User ID</label>
          <input
            className="border px-2 py-1 rounded"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Min Points</label>
          <input
            type="number"
            className="border px-2 py-1 rounded"
            value={minPoints}
            onChange={(e) => setMinPoints(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm">Max Points</label>
          <input
            type="number"
            className="border px-2 py-1 rounded"
            value={maxPoints}
            onChange={(e) => setMaxPoints(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Page</label>
          <input
            type="number"
            value={page}
            onChange={(e) => setPage(Math.max(1, parseInt(e.target.value || '1', 10)))}
            className="border px-2 py-1 rounded w-20"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Limit</label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="border px-2 py-1 rounded"
          >
            {[10, 25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-auto rounded">
        <table className="min-w-full border border-gray-300 border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-center cursor-pointer"
                onClick={() => changeSort('USER_POINTS_ID')}
              >
                USER_POINTS_ID
              </th>
              <th
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-center cursor-pointer"
                onClick={() => changeSort('USER_ID')}
              >
                USER_ID
              </th>
              <th
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-left cursor-pointer"
                onClick={() => changeSort('LOGIN_ID')}
              >
                Username
              </th>
              <th
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer"
                onClick={() => changeSort('POINTS')}
              >
                POINTS
              </th>
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-left">
                FIRST_NAME
              </th>
              <th className="border border-gray-300 px-3 py-2 font-semibold text-gray-700 text-left">
                EMAIL_ID
              </th>
            </tr>
            <tr className="bg-white">
              <th className="border border-gray-200 p-1"></th>
              <th className="border border-gray-200 p-1">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1 text-center"
                  placeholder="User ID"
                  value={colUserId}
                  onChange={(e) => setColUserId(e.target.value)}
                />
              </th>
              <th className="border border-gray-200 p-1">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1"
                  placeholder="Username"
                  value={colUsername}
                  onChange={(e) => setColUsername(e.target.value)}
                />
              </th>
              <th className="border border-gray-200 p-1">
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1 text-right"
                  placeholder=">= Points"
                  value={colPoints}
                  onChange={(e) => setColPoints(e.target.value)}
                />
              </th>
              <th className="border border-gray-200 p-1"></th>
              <th className="border border-gray-200 p-1"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="p-3 text-center border border-gray-200" colSpan={6}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="p-3 text-center border border-gray-200" colSpan={6}>
                  No data
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.USER_POINTS_ID} className="odd:bg-white even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2 text-center align-middle">
                    {r.USER_POINTS_ID}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-center align-middle">
                    {r.USER_ID}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-left align-middle">
                    {r.username || '-'}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-right align-middle">
                    {r.POINTS}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-left align-middle">
                    {r.FIRST_NAME || '-'}
                  </td>
                  <td className="border border-gray-200 px-3 py-2 text-left align-middle">
                    {r.EMAIL_ID || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div>
          Total: {total} • Pages: {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 border rounded"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <button
            className="px-3 py-1 border rounded"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
